const { teamMembers } = require('../profile/index.cjs');

const BASE_URL = 'https://paioneer-labs.eu';
const DEFAULT_LANGUAGE = 'en';
const teamPhotoUrl = slug => `${BASE_URL}/static/assets/team/${slug}.jpg`;
const FOUNDER_SLUG = 'adrian-eisenmeier';
// Link previews show the company logo; a profile page shows the member's portrait.
const OG_IMAGE = `${BASE_URL}/static/assets/brand/paioneer-og.png`;
const LOGO_IMAGE = `${BASE_URL}/static/assets/brand/paioneer-logo-web.png`;

const languages = [
  { code: 'de', locale: 'de_DE', hreflang: 'de', name: 'Deutsch' },
  { code: 'en', locale: 'en_GB', hreflang: 'en', name: 'English' },
  { code: 'nl', locale: 'nl_NL', hreflang: 'nl', name: 'Nederlands' },
  { code: 'uk', locale: 'uk_UA', hreflang: 'uk', name: 'Українська' },
  { code: 'fr', locale: 'fr_FR', hreflang: 'fr', name: 'Français' },
  { code: 'it', locale: 'it_IT', hreflang: 'it', name: 'Italiano' },
  { code: 'es', locale: 'es_ES', hreflang: 'es', name: 'Español' },
  { code: 'sv', locale: 'sv_SE', hreflang: 'sv', name: 'Svenska' },
  { code: 'fi', locale: 'fi_FI', hreflang: 'fi', name: 'Suomi' },
  { code: 'ja', locale: 'ja_JP', hreflang: 'ja', name: '日本語' },
  { code: 'pl', locale: 'pl_PL', hreflang: 'pl', name: 'Polski' },
  { code: 'bg', locale: 'bg_BG', hreflang: 'bg', name: 'Български' }
];

const languageCodes = languages.map(({ code }) => code);

// One page per team member below the team overview; the page id is the slug path.
const memberPageId = slug => `team/${slug}`;
const memberPages = Object.fromEntries(teamMembers.map(member => [
  memberPageId(member.slug),
  { slug: `team/${member.slug}`, priority: '0.85', changefreq: 'monthly', type: 'ProfilePage', member: member.slug }
]));

const pages = {
  home: { slug: '', priority: '1.0', changefreq: 'weekly', type: 'WebSite' },
  team: { slug: 'team', priority: '0.9', changefreq: 'monthly', type: 'AboutPage' },
  ...memberPages,
  events: { slug: 'events', priority: '0.6', changefreq: 'monthly', type: 'CollectionPage' },
  kyveil: { slug: 'kyveil', priority: '0.9', changefreq: 'weekly', type: 'SoftwareApplication' },
  finance: { slug: 'finance', priority: '0.88', changefreq: 'monthly', type: 'SoftwareApplication' },
  agenticdev: { slug: 'agenticdev', priority: '0.86', changefreq: 'monthly', type: 'Service' },
  veridion: { slug: 'veridion', priority: '0.88', changefreq: 'monthly', type: 'SoftwareApplication' },
  quantum: { slug: 'quantum', priority: '0.86', changefreq: 'monthly', type: 'Service' },
  teaching: { slug: 'teaching', priority: '0.78', changefreq: 'monthly', type: 'Service' },
  defenceAiEurope: { slug: 'defence-ai-europe', priority: '0.84', changefreq: 'monthly', type: 'Service', market: 'europe', cluster: 'defenceai' },
  aiConsultingEurope: { slug: 'ai-consulting-europe', priority: '0.8', changefreq: 'monthly', type: 'Service', market: 'europe', cluster: 'ai' },
  kyveilEurope: { slug: 'kyveil-pii-anonymization-europe', priority: '0.78', changefreq: 'monthly', type: 'SoftwareApplication', market: 'europe', cluster: 'kyveil' },
  privacy: { slug: 'privacy', priority: '0.3', changefreq: 'yearly', type: 'WebPage' },
  aiTransparency: { slug: 'ai-transparency', priority: '0.3', changefreq: 'yearly', type: 'WebPage' },
  impressum: { slug: 'impressum', priority: '0.3', changefreq: 'yearly', type: 'WebPage' }
};

const navPageIds = ['home', 'team', 'veridion', 'agenticdev', 'kyveil', 'teaching', 'finance', 'quantum'];
const indexablePageIds = Object.keys(pages);
const strategicPageIds = ['defenceAiEurope', 'aiConsultingEurope', 'kyveilEurope'];

const localized = {
  en: {
    siteTagline: 'Local AI software, data protection and software architecture',
    homeTitle: 'pAIoneer Labs GmbH - Local AI Solutions with Data Protection in Focus',
    homeDescription: 'pAIoneer Labs GmbH develops local AI software with data protection in focus: Kyveil PII anonymization, the company AI Veridion Agentic Company Intelligence, Intent-Driven Development, AI training and an AI trading assistant.',
    teamTitle: 'Team | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier and Nikolai Genov founded pAIoneer Labs GmbH and run it as managing directors; Alina Tselinina is senior software developer in the team.',
    eventsTitle: 'Events | pAIoneer Labs GmbH',
    eventsDescription: 'Events in which pAIoneer Labs GmbH takes part or has taken part.',
    kyveilTitle: 'Kyveil PII Anonymization | Local Data Protection Software',
    kyveilDescription: 'Kyveil detects and anonymizes personally identifiable information locally in documents and datasets before AI, testing, analytics or external sharing.',
    iddTitle: 'Intent-Driven Development (IDD) for Agentic Coding',
    iddDescription: 'IDD makes AI-assisted software development controllable by turning goals, non-goals, decisions and scenarios into operational project memory for coding agents.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion is a complete organisation of AI staff in business units: it plans in fixed cycles, delivers verified results around the clock, builds missing AI systems itself and runs on premises or in your own cloud.',
    quantumTitle: "Sovereign LLMs & Quantum AI | pAIoneer Labs GmbH",
    quantumDescription: "Our goal from Germany: under Veridion ('AI develops AI further'), developing compact sovereign language models and Quantum Transformers that will compete with large models from the US and China.",
    teachingTitle: 'AI Training, Edge AI and EU AI Act Workshops',
    teachingDescription: 'Practical AI training for executives, project managers and developers: LLMs, RAG, edge AI, AI Act compliance, security and implementation strategy.',
    financeTitle: 'AI Trading Assistant: Conversational Data Analysis, Forecasts and Sentiment',
    financeDescription: 'The AI trading assistant connects your brokerage account and answers questions about your positions in plain language, with analyses of prices, forecasts and financial news and a machine-learning model for each financial instrument.',
    privacyTitle: 'Privacy Policy | pAIoneer Labs GmbH',
    privacyDescription: 'Privacy information for paioneer-labs.eu, cookies, local services, Kyveil presentation, downloads and professional contact.',
    aiTransparencyTitle: 'AI Transparency | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'How pAIoneer Labs GmbH uses artificial intelligence and provides information about it: AI assistant, labelling of AI-generated content, editorial responsibility and contact under the AI Act.',
    impressumTitle: 'Legal Notice | pAIoneer Labs GmbH',
    impressumDescription: 'Legal provider information for pAIoneer Labs GmbH.',
    europe: 'Europe',
    market: 'market',
    defence: 'defence, defense, critical infrastructure, force protection, ports, energy sites, depots and border-adjacent facilities',
    defenceAi: 'Defence AI consulting for Europe: local LLMs, on-premises agentic development, classified-data workflows, edge inference, RAG evaluation and controlled AI implementation.',
    aiConsulting: 'AI consulting in Europe for practical use cases, ROI evaluation, local LLM deployment, RAG and GraphRAG architecture, edge AI, AI Act readiness and software integration.',
    kyveilEurope: 'Kyveil PII anonymization for Europe: local GDPR-ready document anonymization before AI workflows, tests, analytics, datasets and external sharing.',
    sectionsTitle: 'Core topics',
    relatedTitle: 'Related focus pages',
    contactCta: 'Contact pAIoneer Labs GmbH'
  },
  de: {
    siteTagline: 'Lokale KI-Software, Datenschutz und Softwarearchitektur',
    homeTitle: 'pAIoneer Labs GmbH - Lokale KI-Lösungen mit Datenschutz im Fokus',
    homeDescription: 'Die pAIoneer Labs GmbH entwickelt lokale KI-Software mit Datenschutz im Fokus: Kyveil PII-Anonymisierung, die Firmen-KI Veridion Agentic Company Intelligence, Intent-Driven Development, KI-Training und einen KI-Trading-Assistenten.',
    teamTitle: 'Team | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier und Nikolai Genov haben die pAIoneer Labs GmbH gegründet und führen sie als Geschäftsführer; Alina Tselinina gehört als Senior Software Developer zum Team.',
    eventsTitle: 'Events | pAIoneer Labs GmbH',
    eventsDescription: 'Veranstaltungen, an denen die pAIoneer Labs GmbH teilnimmt oder teilgenommen hat.',
    kyveilTitle: 'Kyveil PII-Anonymisierung | Lokale Datenschutzsoftware',
    kyveilDescription: 'Kyveil erkennt und anonymisiert personenbezogene Daten lokal in Dokumenten und Datensätzen vor AI, Tests, Analysen oder externer Weitergabe.',
    iddTitle: 'Intent-Driven Development (IDD) für Agentic Coding',
    iddDescription: 'IDD macht AI-assisted Softwareentwicklung kontrollierbar, indem Ziele, Nicht-Ziele, Entscheidungen und Szenarien zu nutzbarem Projektgedächtnis für Coding Agents werden.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion ist eine vollständige Organisation aus KI-Mitarbeitern in Business Units: Sie plant in festen Zyklen, liefert rund um die Uhr geprüfte Ergebnisse, baut fehlende KI-Systeme selbst und läuft on-premises oder in der eigenen Cloud.',
    quantumTitle: "Souveräne LLMs & Quanten-KI | pAIoneer Labs GmbH",
    quantumDescription: "Unser Ziel aus Deutschland heraus: unter Veridion („KI entwickelt KI weiter“) eigene kompakte Sprachmodelle und Quanten-Transformer entwickeln, die künftig mit großen Modellen aus den USA und China konkurrieren werden.",
    teachingTitle: 'KI-Training, Edge AI und EU-AI-Act-Workshops',
    teachingDescription: 'Praktische KI-Trainings für Geschäftsführung, Projektleitung und Entwicklung: LLMs, RAG, Edge AI, AI-Act-Compliance, Security und Umsetzung.',
    financeTitle: 'KI-Trading-Assistent: Datenanalyse im Gespräch, Prognosen und Sentiment',
    financeDescription: 'Der KI-Trading-Assistent bindet das eigene Wertpapierdepot an und beantwortet Fragen zu den Positionen in natürlicher Sprache, mit Analysen auf Kursen, Prognosen und Finanznachrichten und einem Machine-Learning-Modell je Finanzinstrument.',
    privacyTitle: 'Datenschutzerklärung | pAIoneer Labs GmbH',
    privacyDescription: 'Datenschutzinformationen für paioneer-labs.eu, Cookies, lokale Dienste, Kyveil-Präsentation, Downloads und professionelle Kontaktaufnahme.',
    aiTransparencyTitle: 'KI-Transparenz | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Wie die pAIoneer Labs GmbH künstliche Intelligenz einsetzt und darüber informiert: KI-Assistent, Kennzeichnung KI-erzeugter Inhalte, redaktionelle Verantwortung und Ansprechpartner nach der KI-Verordnung.',
    impressumTitle: 'Impressum | pAIoneer Labs GmbH',
    impressumDescription: 'Rechtliche Anbieterinformationen für die pAIoneer Labs GmbH.',
    europe: 'Europa',
    market: 'Markt',
    defence: 'Verteidigung, kritische Infrastruktur, Force Protection, Häfen, Energieanlagen, Depots und grenznahe Standorte',
    defenceAi: 'Defence-AI-Beratung für Europa: lokale LLMs, On-Prem-Agentic-Development, Workflows mit schutzbedürftigen Daten, Edge Inference, RAG-Evaluation und kontrollierte KI-Umsetzung.',
    aiConsulting: 'KI-Beratung in Europa für Use Cases, ROI-Bewertung, lokale LLM-Deployments, RAG- und GraphRAG-Architekturen, Edge AI, AI-Act-Readiness und Softwareintegration.',
    kyveilEurope: 'Kyveil PII-Anonymisierung für Europa: lokale DSGVO-fähige Dokumentenanonymisierung vor KI-Workflows, Tests, Analysen, Datensätzen und externer Weitergabe.',
    sectionsTitle: 'Kernbereiche',
    relatedTitle: 'Verwandte Fokus-Seiten',
    contactCta: 'pAIoneer Labs GmbH kontaktieren'
  },
  nl: {
    siteTagline: 'Lokale AI-software, gegevensbescherming en softwarearchitectuur',
    homeTitle: 'pAIoneer Labs GmbH - Lokale AI-oplossingen waarin gegevensbescherming centraal staat',
    homeDescription: 'pAIoneer Labs GmbH ontwikkelt lokale AI-software waarin gegevensbescherming centraal staat: PII-anonimisering met Kyveil, de bedrijfs-AI Veridion Agentic Company Intelligence, Intent-Driven Development, AI-trainingen en een AI-tradingassistent.',
    teamTitle: 'Team | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier en Nikolai Genov hebben pAIoneer Labs GmbH opgericht en leiden het bedrijf als directeuren; Alina Tselinina maakt als senior softwareontwikkelaar deel uit van het team.',
    eventsTitle: 'Evenementen | pAIoneer Labs GmbH',
    eventsDescription: 'Evenementen waaraan pAIoneer Labs GmbH deelneemt of heeft deelgenomen.',
    kyveilTitle: 'Kyveil PII-anonimisering | Lokale software voor gegevensbescherming',
    kyveilDescription: 'Kyveil herkent en anonimiseert persoonsgegevens lokaal in documenten en datasets, vóór gebruik in AI, tests en analyses of vóór externe uitwisseling.',
    iddTitle: 'Intent-Driven Development (IDD) voor Agentic Coding',
    iddDescription: 'IDD maakt AI-ondersteunde softwareontwikkeling bestuurbaar door doelen, niet-doelen, beslissingen en scenario’s vast te leggen als operationeel projectgeheugen voor code-agenten.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion is een volledige organisatie van AI-medewerkers in business units: ze plant in vaste cycli, levert de klok rond gecontroleerde resultaten, bouwt ontbrekende AI-systemen zelf en draait on-premises of in uw eigen cloud.',
    quantumTitle: "Soevereine LLM's & Quantum-AI | pAIoneer Labs GmbH",
    quantumDescription: "Ons doel vanuit Duitsland: onder Veridion ('AI ontwikkelt AI verder') eigen compacte taalmodellen en Quantum Transformers ontwikkelen die straks concurreren met grote modellen uit de VS en China.",
    teachingTitle: 'AI-trainingen, edge-AI en workshops over de EU AI Act',
    teachingDescription: 'Praktijkgerichte AI-trainingen voor directie, projectleiders en ontwikkelaars: LLM’s, RAG, edge-AI, naleving van de AI Act, beveiliging en implementatiestrategie.',
    financeTitle: 'AI-tradingassistent: data-analyse in gesprek, prognoses en sentiment',
    financeDescription: 'De AI-tradingassistent koppelt uw effectenrekening en beantwoordt vragen over uw posities in gewone taal, met analyses van koersen, prognoses en financieel nieuws en een machine-learningmodel voor elk financieel instrument.',
    privacyTitle: 'Privacyverklaring | pAIoneer Labs GmbH',
    privacyDescription: 'Privacy-informatie over paioneer-labs.eu, cookies, lokale diensten, de presentatie van Kyveil, downloads en zakelijk contact.',
    aiTransparencyTitle: 'AI-transparantie | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Hoe pAIoneer Labs GmbH kunstmatige intelligentie inzet en daarover informeert: AI-assistent, markering van door AI gegenereerde inhoud, redactionele verantwoordelijkheid en aanspreekpunt volgens de AI-verordening.',
    impressumTitle: 'Colofon | pAIoneer Labs GmbH',
    impressumDescription: 'Wettelijke gegevens over de aanbieder pAIoneer Labs GmbH.',
    europe: 'Europa',
    market: 'markt',
    defence: 'defensie, kritieke infrastructuur, force protection, havens, energielocaties, depots en locaties nabij de grens',
    defenceAi: 'AI-advies voor defensie in Europa: lokale LLM’s, agentische softwareontwikkeling on-premises, workflows met gerubriceerde gegevens, edge-inferentie, RAG-evaluatie en gecontroleerde AI-implementatie.',
    aiConsulting: 'AI-advies in Europa voor praktische use cases, ROI-beoordeling, lokale inzet van LLM’s, RAG- en GraphRAG-architecturen, edge-AI, voorbereiding op de AI Act en software-integratie.',
    kyveilEurope: 'Kyveil PII-anonimisering voor Europa: lokale, AVG-conforme anonimisering van documenten vóór AI-workflows, tests, analyses, datasets en externe uitwisseling.',
    sectionsTitle: 'Kernthema’s',
    relatedTitle: 'Verwante themapagina’s',
    contactCta: 'Neem contact op met pAIoneer Labs GmbH'
  },
  uk: {
    siteTagline: 'Локальне програмне забезпечення на основі ШІ, захист даних і програмна архітектура',
    homeTitle: 'pAIoneer Labs GmbH — локальні ШІ-рішення з акцентом на захист даних',
    homeDescription: 'pAIoneer Labs GmbH розробляє локальне програмне забезпечення на основі ШІ з акцентом на захист даних: Kyveil для анонімізації персональних даних, корпоративний ШІ Veridion Agentic Company Intelligence, Intent-Driven Development, навчання із ШІ та ШІ-асистент для трейдингу.',
    teamTitle: 'Команда | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier та Nikolai Genov заснували pAIoneer Labs GmbH і очолюють її як керуючі директори; Alina Tselinina входить до команди як Senior Software Developer.',
    eventsTitle: 'Події | pAIoneer Labs GmbH',
    eventsDescription: 'Події, у яких pAIoneer Labs GmbH бере або брала участь.',
    kyveilTitle: 'Анонімізація PII з Kyveil | Локальне програмне забезпечення для захисту даних',
    kyveilDescription: 'Kyveil локально виявляє й анонімізує персональні дані в документах і наборах даних перед використанням у ШІ, тестуванням, аналітикою або передаванням назовні.',
    iddTitle: 'Intent-Driven Development (IDD) для агентного програмування',
    iddDescription: 'IDD робить розробку програмного забезпечення з підтримкою ШІ керованою: цілі, нецілі, рішення та сценарії стають операційною пам’яттю проєкту для агентів, що пишуть код.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion — повноцінна організація зі співробітників-ШІ в бізнес-підрозділах: планує у фіксованих циклах, цілодобово постачає перевірені результати, сама будує відсутні ШІ-системи й працює локально або у вашій хмарі.',
    quantumTitle: "Суверенні LLM та Квантовий ШІ | pAIoneer Labs GmbH",
    quantumDescription: "Наша мета з Німеччини: під керуванням Veridion («ШІ розвиває ШІ далі») створити власні компактні мовні моделі та квантові трансформери, які конкуруватимуть із великими моделями США та Китаю.",
    teachingTitle: 'Навчання із ШІ, периферійний ШІ (Edge AI) та воркшопи з EU AI Act',
    teachingDescription: 'Практичне навчання із ШІ для керівництва, керівників проєктів і розробників: LLM, RAG, периферійний ШІ, відповідність EU AI Act, безпека та стратегія впровадження.',
    financeTitle: 'ШІ-асистент для трейдингу: аналіз даних у розмові, прогнози та тональність',
    financeDescription: 'ШІ-асистент для трейдингу підключає ваш брокерський рахунок і звичайною мовою відповідає на запитання про ваші позиції, аналізуючи курси, прогнози та фінансові новини; для кожного фінансового інструмента працює окрема модель машинного навчання.',
    privacyTitle: 'Політика конфіденційності | pAIoneer Labs GmbH',
    privacyDescription: 'Інформація про захист даних для paioneer-labs.eu: файли cookie, локальні сервіси, представлення Kyveil, завантаження та ділові контакти.',
    aiTransparencyTitle: 'Прозорість у сфері ШІ | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Як pAIoneer Labs GmbH використовує штучний інтелект та інформує про це: ШІ-асистент, маркування контенту, згенерованого ШІ, редакційна відповідальність і контактна особа відповідно до Регламенту про ШІ.',
    impressumTitle: 'Вихідні дані | pAIoneer Labs GmbH',
    impressumDescription: 'Правова інформація про постачальника послуг pAIoneer Labs GmbH.',
    europe: 'Європа',
    market: 'ринок',
    defence: 'оборона, критична інфраструктура, захист сил, порти, енергетичні об’єкти, склади та прикордонні об’єкти',
    defenceAi: 'Консультування з оборонного ШІ для Європи: локальні LLM, агентна розробка у власній інфраструктурі, робочі процеси з даними з обмеженим доступом, інференс на периферійних пристроях, оцінювання RAG і контрольоване впровадження ШІ.',
    aiConsulting: 'Консультування із ШІ в Європі: практичні сценарії використання, оцінювання ROI, локальне розгортання LLM, архітектури RAG і GraphRAG, периферійний ШІ, готовність до EU AI Act та інтеграція програмного забезпечення.',
    kyveilEurope: 'Анонімізація PII з Kyveil для Європи: локальна анонімізація документів відповідно до GDPR перед робочими процесами із ШІ, тестами, аналітикою, формуванням наборів даних і передаванням назовні.',
    sectionsTitle: 'Ключові теми',
    relatedTitle: 'Пов’язані тематичні сторінки',
    contactCta: 'Зв’язатися з pAIoneer Labs GmbH'
  },
  fr: {
    siteTagline: 'Logiciels d’IA locaux, protection des données et architecture logicielle',
    homeTitle: 'pAIoneer Labs GmbH - Solutions d’IA locales centrées sur la protection des données',
    homeDescription: 'pAIoneer Labs GmbH développe des logiciels d’IA locaux centrés sur la protection des données : anonymisation des données personnelles avec Kyveil, l’IA d’entreprise Veridion Agentic Company Intelligence, Intent-Driven Development, formation à l’IA et assistant de trading IA.',
    teamTitle: 'Équipe | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier et Nikolai Genov ont fondé pAIoneer Labs GmbH et la dirigent en tant que gérants ; Alina Tselinina fait partie de l’équipe en tant que Senior Software Developer.',
    eventsTitle: 'Événements | pAIoneer Labs GmbH',
    eventsDescription: 'Événements auxquels pAIoneer Labs GmbH participe ou a participé.',
    kyveilTitle: 'Kyveil : anonymisation des données personnelles | Logiciel local de protection des données',
    kyveilDescription: 'Kyveil détecte et anonymise localement les données personnelles (PII) dans les documents et les jeux de données, avant leur utilisation par l’IA, pour des tests ou des analyses, ou leur partage externe.',
    iddTitle: 'Intent-Driven Development (IDD) pour le codage agentique',
    iddDescription: 'IDD rend le développement logiciel assisté par l’IA maîtrisable en transformant les objectifs, les non-objectifs, les décisions et les scénarios en mémoire de projet opérationnelle pour les agents de codage.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion est une organisation complète de collaborateurs IA en business units : elle planifie en cycles fixes, livre jour et nuit des résultats vérifiés, se construit les systèmes d’IA manquants et fonctionne sur site ou dans votre propre cloud.',
    quantumTitle: "LLM Souverains & IA Quantique | pAIoneer Labs GmbH",
    quantumDescription: "Notre objectif depuis l'Allemagne : sous Veridion (« l'IA fait évoluer l'IA »), développer des modèles de langage compacts et des Quantum Transformers qui rivaliseront avec les grands modèles américains et chinois.",
    teachingTitle: 'Formation à l’IA, IA en périphérie et ateliers sur l’EU AI Act',
    teachingDescription: 'Formations pratiques à l’IA pour dirigeants, chefs de projet et développeurs : LLM, RAG, IA en périphérie, conformité à l’AI Act, sécurité et stratégie de mise en œuvre.',
    financeTitle: 'Assistant de trading IA : analyse de données en conversation, prévisions et sentiment',
    financeDescription: 'L’assistant de trading IA se connecte à votre compte-titres et répond en langage naturel à vos questions sur vos positions, avec des analyses des cours, des prévisions et de l’actualité financière, ainsi qu’un modèle d’apprentissage automatique pour chaque instrument financier.',
    privacyTitle: 'Politique de confidentialité | pAIoneer Labs GmbH',
    privacyDescription: 'Informations sur la protection des données pour paioneer-labs.eu : cookies, services locaux, présentation de Kyveil, téléchargements et prise de contact professionnelle.',
    aiTransparencyTitle: 'Transparence en matière d’IA | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Comment pAIoneer Labs GmbH utilise l’intelligence artificielle et informe à ce sujet : assistant IA, marquage des contenus générés par l’IA, responsabilité éditoriale et interlocuteur au titre du règlement sur l’intelligence artificielle.',
    impressumTitle: 'Mentions légales | pAIoneer Labs GmbH',
    impressumDescription: 'Informations légales sur le fournisseur pAIoneer Labs GmbH.',
    europe: 'Europe',
    market: 'marché',
    defence: 'défense, infrastructures critiques, protection des forces, ports, sites énergétiques, dépôts et installations proches des frontières',
    defenceAi: 'Conseil en IA de défense pour l’Europe : LLM locaux, développement agentique sur site, flux de travail avec des données classifiées, inférence en périphérie, évaluation RAG et mise en œuvre maîtrisée de l’IA.',
    aiConsulting: 'Conseil en IA en Europe pour des cas d’usage concrets : évaluation du ROI, déploiement de LLM locaux, architectures RAG et GraphRAG, IA en périphérie, préparation à l’AI Act et intégration logicielle.',
    kyveilEurope: 'Kyveil, l’anonymisation des données personnelles pour l’Europe : anonymisation locale des documents, conforme au RGPD, avant les flux de travail IA, les tests, les analyses, les jeux de données et le partage externe.',
    sectionsTitle: 'Thèmes clés',
    relatedTitle: 'Pages thématiques associées',
    contactCta: 'Contacter pAIoneer Labs GmbH'
  },
  it: {
    siteTagline: 'Software di IA locale, protezione dei dati e architettura software',
    homeTitle: 'pAIoneer Labs GmbH - Soluzioni di IA locali con la protezione dei dati al centro',
    homeDescription: 'pAIoneer Labs GmbH sviluppa software di IA locale con la protezione dei dati al centro: anonimizzazione dei dati personali con Kyveil, l’IA aziendale Veridion Agentic Company Intelligence, Intent-Driven Development, formazione sull’IA e un assistente di trading basato sull’IA.',
    teamTitle: 'Team | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier e Nikolai Genov hanno fondato pAIoneer Labs GmbH e la dirigono come amministratori delegati; Alina Tselinina fa parte del team come Senior Software Developer.',
    eventsTitle: 'Eventi | pAIoneer Labs GmbH',
    eventsDescription: 'Eventi a cui pAIoneer Labs GmbH partecipa o ha partecipato.',
    kyveilTitle: 'Anonimizzazione PII con Kyveil | Software locale per la protezione dei dati',
    kyveilDescription: 'Kyveil rileva e anonimizza in locale i dati personali contenuti in documenti e dataset, prima che vengano usati per IA, test, analisi o condivisione esterna.',
    iddTitle: 'Intent-Driven Development (IDD) per l’agentic coding',
    iddDescription: 'L’IDD rende governabile lo sviluppo software assistito dall’IA trasformando obiettivi, non-obiettivi, decisioni e scenari in una memoria di progetto operativa per gli agenti di programmazione.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion è un’organizzazione completa di collaboratori IA in business unit: pianifica in cicli fissi, consegna giorno e notte risultati verificati, si costruisce i sistemi di IA mancanti e funziona on-premises o nel proprio cloud.',
    quantumTitle: "LLM Sovrani & IA Quantistica | pAIoneer Labs GmbH",
    quantumDescription: "Il nostro obiettivo dalla Germania: sotto Veridion («l'IA sviluppa ulteriormente l'IA»), sviluppare modelli linguistici compatti e Quantum Transformer che competeranno con i grandi modelli USA e cinesi.",
    teachingTitle: 'Formazione sull’IA, Edge AI e workshop sull’EU AI Act',
    teachingDescription: 'Formazione pratica sull’IA per dirigenti, responsabili di progetto e sviluppatori: LLM, RAG, edge AI, conformità all’AI Act, sicurezza e strategia di implementazione.',
    financeTitle: 'Assistente di trading basato sull’IA: analisi dei dati in conversazione, previsioni e sentiment',
    financeDescription: 'L’assistente di trading basato sull’IA collega il tuo conto titoli e risponde in linguaggio naturale alle domande sulle tue posizioni, con analisi di prezzi, previsioni e notizie finanziarie e un modello di machine learning per ogni strumento finanziario.',
    privacyTitle: 'Informativa sulla privacy | pAIoneer Labs GmbH',
    privacyDescription: 'Informativa sulla protezione dei dati per paioneer-labs.eu: cookie, servizi locali, presentazione di Kyveil, download e contatti professionali.',
    aiTransparencyTitle: 'Trasparenza sull’IA | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Come pAIoneer Labs GmbH utilizza l’intelligenza artificiale e fornisce informazioni al riguardo: assistente IA, marcatura dei contenuti generati dall’IA, responsabilità editoriale e punto di contatto ai sensi del regolamento sull’intelligenza artificiale.',
    impressumTitle: 'Note legali | pAIoneer Labs GmbH',
    impressumDescription: 'Informazioni legali sul fornitore pAIoneer Labs GmbH.',
    europe: 'Europa',
    market: 'mercato',
    defence: 'difesa, infrastrutture critiche, protezione delle forze, porti, siti energetici, depositi e strutture in prossimità dei confini',
    defenceAi: 'Consulenza sull’IA per la difesa in Europa: LLM locali, sviluppo agentico on-premises, flussi di lavoro con dati classificati, inferenza edge, valutazione RAG e implementazione controllata dell’IA.',
    aiConsulting: 'Consulenza sull’IA in Europa per casi d’uso concreti, valutazione del ROI, deployment di LLM locali, architetture RAG e GraphRAG, edge AI, preparazione all’AI Act e integrazione software.',
    kyveilEurope: 'Anonimizzazione PII con Kyveil per l’Europa: anonimizzazione locale dei documenti conforme al GDPR prima di flussi di lavoro IA, test, analisi, dataset e condivisione esterna.',
    sectionsTitle: 'Temi principali',
    relatedTitle: 'Pagine tematiche correlate',
    contactCta: 'Contatta pAIoneer Labs GmbH'
  },
  es: {
    siteTagline: 'Software de IA local, protección de datos y arquitectura de software',
    homeTitle: 'pAIoneer Labs GmbH - Soluciones de IA locales centradas en la protección de datos',
    homeDescription: 'pAIoneer Labs GmbH desarrolla software de IA local centrado en la protección de datos: anonimización de datos personales con Kyveil, la IA corporativa Veridion Agentic Company Intelligence, Intent-Driven Development, formación en IA y un asistente de trading con IA.',
    teamTitle: 'Equipo | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier y Nikolai Genov fundaron pAIoneer Labs GmbH y la dirigen como directores generales; Alina Tselinina forma parte del equipo como Senior Software Developer.',
    eventsTitle: 'Eventos | pAIoneer Labs GmbH',
    eventsDescription: 'Eventos en los que pAIoneer Labs GmbH participa o ha participado.',
    kyveilTitle: 'Anonimización de PII con Kyveil | Software local de protección de datos',
    kyveilDescription: 'Kyveil detecta y anonimiza de forma local los datos personales de documentos y conjuntos de datos antes de utilizarlos en IA, pruebas o análisis, o de compartirlos con terceros.',
    iddTitle: 'Intent-Driven Development (IDD) para Agentic Coding',
    iddDescription: 'IDD hace controlable el desarrollo de software asistido por IA al convertir los objetivos, los no objetivos, las decisiones y los escenarios en una memoria operativa del proyecto para los agentes de programación.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion es una organización completa de colaboradores de IA en unidades de negocio: planifica en ciclos fijos, entrega resultados verificados las veinticuatro horas, se construye los sistemas de IA que faltan y funciona en sus instalaciones o en su propia nube.',
    quantumTitle: "LLM Soberanos e IA Cuántica | pAIoneer Labs GmbH",
    quantumDescription: "Nuestro objetivo desde Alemania: bajo Veridion («la IA desarrolla aún más la IA»), desarrollar modelos de lenguaje compactos y Quantum Transformers que competirán con los grandes modelos de EE. UU. y China.",
    teachingTitle: 'Formación en IA, IA en el edge y talleres sobre el EU AI Act',
    teachingDescription: 'Formación práctica en IA para directivos, responsables de proyecto y desarrolladores: LLM, RAG, IA en el edge, cumplimiento del AI Act, seguridad y estrategia de implementación.',
    financeTitle: 'Asistente de trading con IA: análisis de datos en conversación, previsiones y sentimiento',
    financeDescription: 'El asistente de trading con IA se conecta a su cuenta de valores y responde en lenguaje natural a preguntas sobre sus posiciones, con análisis de cotizaciones, previsiones y noticias financieras y un modelo de aprendizaje automático para cada instrumento financiero.',
    privacyTitle: 'Política de privacidad | pAIoneer Labs GmbH',
    privacyDescription: 'Información sobre protección de datos de paioneer-labs.eu: cookies, servicios locales, presentación de Kyveil, descargas y contacto profesional.',
    aiTransparencyTitle: 'Transparencia en materia de IA | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Cómo utiliza pAIoneer Labs GmbH la inteligencia artificial e informa sobre ello: asistente de IA, marcado de los contenidos generados por IA, responsabilidad editorial y persona de contacto conforme al Reglamento de Inteligencia Artificial.',
    impressumTitle: 'Aviso legal | pAIoneer Labs GmbH',
    impressumDescription: 'Información legal sobre el prestador de servicios pAIoneer Labs GmbH.',
    europe: 'Europa',
    market: 'mercado',
    defence: 'defensa, infraestructuras críticas, protección de la fuerza, puertos, instalaciones energéticas, depósitos e instalaciones próximas a fronteras',
    defenceAi: 'Consultoría de IA para defensa en Europa: LLM locales, desarrollo agéntico en infraestructura propia, flujos de trabajo con datos clasificados, inferencia en el edge, evaluación de RAG e implementación controlada de la IA.',
    aiConsulting: 'Consultoría de IA en Europa para casos de uso prácticos, evaluación del ROI, despliegue local de LLM, arquitecturas RAG y GraphRAG, IA en el edge, preparación para el AI Act e integración de software.',
    kyveilEurope: 'Anonimización de PII con Kyveil para Europa: anonimización local de documentos preparada para el RGPD antes de los flujos de trabajo de IA, las pruebas, los análisis, los conjuntos de datos y la cesión a terceros.',
    sectionsTitle: 'Temas centrales',
    relatedTitle: 'Páginas temáticas relacionadas',
    contactCta: 'Contactar con pAIoneer Labs GmbH'
  },
  sv: {
    siteTagline: 'Lokal AI-programvara, dataskydd och mjukvaruarkitektur',
    homeTitle: 'pAIoneer Labs GmbH - Lokala AI-lösningar med dataskydd i fokus',
    homeDescription: 'pAIoneer Labs GmbH utvecklar lokal AI-programvara med dataskydd i fokus: Kyveil för anonymisering av personuppgifter, företags-AI:n Veridion Agentic Company Intelligence, Intent-Driven Development, AI-utbildning och en AI-tradingassistent.',
    teamTitle: 'Team | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier och Nikolai Genov har grundat pAIoneer Labs GmbH och leder bolaget som verkställande direktörer; Alina Tselinina ingår i teamet som Senior Software Developer.',
    eventsTitle: 'Evenemang | pAIoneer Labs GmbH',
    eventsDescription: 'Evenemang som pAIoneer Labs GmbH deltar i eller har deltagit i.',
    kyveilTitle: 'Kyveil PII-anonymisering | Lokal programvara för dataskydd',
    kyveilDescription: 'Kyveil identifierar och anonymiserar personuppgifter lokalt i dokument och datamängder före AI, tester, analyser eller extern delning.',
    iddTitle: 'Intent-Driven Development (IDD) för Agentic Coding',
    iddDescription: 'IDD gör AI-stödd mjukvaruutveckling kontrollerbar genom att mål, icke-mål, beslut och scenarier blir ett operativt projektminne för kodagenter.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion är en komplett organisation av AI-medarbetare i affärsområden: den planerar i fasta cykler, levererar verifierade resultat dygnet runt, bygger själv de AI-system som saknas och körs on-premises eller i ert eget moln.',
    quantumTitle: "Suveräna LLM & Kvant-AI | pAIoneer Labs GmbH",
    quantumDescription: "Vårt mål från Tyskland: att under Veridion ('AI utvecklar AI vidare') utveckla kompakta språkmodeller och Quantum Transformers som i framtiden kommer att konkurrera med de stora modellerna från USA och Kina.",
    teachingTitle: 'AI-utbildning, edge-AI och workshoppar om EU AI Act',
    teachingDescription: 'Praktisk AI-utbildning för företagsledning, projektledare och utvecklare: LLM:er, RAG, edge-AI, efterlevnad av AI Act, säkerhet och implementeringsstrategi.',
    financeTitle: 'AI-tradingassistent: dataanalys i samtal, prognoser och sentiment',
    financeDescription: 'AI-tradingassistenten ansluter din värdepappersdepå och besvarar frågor om dina positioner på vanligt språk, med analyser av kurser, prognoser och finansnyheter och en maskininlärningsmodell för varje finansiellt instrument.',
    privacyTitle: 'Integritetspolicy | pAIoneer Labs GmbH',
    privacyDescription: 'Dataskyddsinformation för paioneer-labs.eu, cookies, lokala tjänster, presentationen av Kyveil, nedladdningar och professionell kontakt.',
    aiTransparencyTitle: 'AI-transparens | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Hur pAIoneer Labs GmbH använder artificiell intelligens och informerar om det: AI-assistent, märkning av AI-genererat innehåll, redaktionellt ansvar och kontaktpunkt enligt AI-förordningen.',
    impressumTitle: 'Juridisk information | pAIoneer Labs GmbH',
    impressumDescription: 'Juridiska uppgifter om leverantören pAIoneer Labs GmbH.',
    europe: 'Europa',
    market: 'marknad',
    defence: 'försvar, kritisk infrastruktur, skydd av egna styrkor, hamnar, energianläggningar, depåer och gränsnära anläggningar',
    defenceAi: 'AI-rådgivning för försvarssektorn i Europa: lokala LLM:er, agentisk utveckling on-premises, arbetsflöden med sekretessklassade data, edge-inferens, RAG-utvärdering och kontrollerad AI-implementering.',
    aiConsulting: 'AI-rådgivning i Europa för praktiska användningsfall, ROI-bedömning, lokal driftsättning av LLM:er, RAG- och GraphRAG-arkitektur, edge-AI, beredskap inför AI Act och mjukvaruintegration.',
    kyveilEurope: 'Kyveil PII-anonymisering för Europa: lokal, GDPR-anpassad dokumentanonymisering före AI-arbetsflöden, tester, analyser, datamängder och extern delning.',
    sectionsTitle: 'Kärnområden',
    relatedTitle: 'Relaterade fokussidor',
    contactCta: 'Kontakta pAIoneer Labs GmbH'
  },
  fi: {
    siteTagline: 'Paikallinen tekoälyohjelmisto, tietosuoja ja ohjelmistoarkkitehtuuri',
    homeTitle: 'pAIoneer Labs GmbH - paikalliset tekoälyratkaisut, joiden keskiössä on tietosuoja',
    homeDescription: 'pAIoneer Labs GmbH kehittää paikallisia tekoälyohjelmistoja, joiden keskiössä on tietosuoja: henkilötietojen anonymisointi Kyveilillä, yrityksen tekoäly Veridion Agentic Company Intelligence, Intent-Driven Development, tekoälykoulutus ja tekoälypohjainen kaupankäyntiavustaja.',
    teamTitle: 'Tiimi | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier ja Nikolai Genov ovat perustaneet pAIoneer Labs GmbH:n ja johtavat sitä toimitusjohtajina; Alina Tselinina kuuluu tiimiin senior-ohjelmistokehittäjänä.',
    eventsTitle: 'Tapahtumat | pAIoneer Labs GmbH',
    eventsDescription: 'Tapahtumat, joihin pAIoneer Labs GmbH osallistuu tai on osallistunut.',
    kyveilTitle: 'Kyveil: PII-anonymisointi | Paikallinen tietosuojaohjelmisto',
    kyveilDescription: 'Kyveil tunnistaa ja anonymisoi henkilötiedot paikallisesti dokumenteista ja data-aineistoista ennen tekoälyn käyttöä, testausta, analytiikkaa tai jakamista ulkopuolisille.',
    iddTitle: 'Intent-Driven Development (IDD) agenttiseen koodaukseen',
    iddDescription: 'IDD tekee tekoälyavusteisesta ohjelmistokehityksestä hallittavaa muuttamalla tavoitteet, ei-tavoitteet, päätökset ja skenaariot koodausagenttien operatiiviseksi projektimuistiksi.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion on täydellinen organisaatio tekoälytyöntekijöitä liiketoimintayksiköissä: se suunnittelee kiinteissä sykleissä, toimittaa todennettuja tuloksia ympäri vuorokauden, rakentaa puuttuvat tekoälyjärjestelmät itse ja toimii omissa tiloissa tai omassa pilvessä.',
    quantumTitle: "Suvereenit LLM:t & Kvantti-tekoäly | pAIoneer Labs GmbH",
    quantumDescription: "Tavoitteemme Saksasta käsin: kehittää Veridionin ('tekoäly kehittää tekoälyä') alaisuudessa kompakteja kielimalleja ja kvanttitransformereita, jotka tulevaisuudessa kilpailevat USA:n ja Kiinan suurten mallien kanssa.",
    teachingTitle: 'Tekoälykoulutus, reunatekoäly ja EU AI Act -työpajat',
    teachingDescription: 'Käytännönläheistä tekoälykoulutusta yritysjohdolle, projektipäälliköille ja kehittäjille: LLM-mallit, RAG, reunatekoäly, EU AI Act -vaatimustenmukaisuus, tietoturva ja toteutusstrategia.',
    financeTitle: 'Tekoälypohjainen kaupankäyntiavustaja: data-analyysia keskustellen, ennusteet ja sentimentti',
    financeDescription: 'Tekoälypohjainen kaupankäyntiavustaja liitetään arvo-osuustiliisi, ja se vastaa positioitasi koskeviin kysymyksiin tavallisella kielellä; taustalla ovat kurssien, ennusteiden ja talousuutisten analyysit sekä oma koneoppimismalli jokaiselle rahoitusinstrumentille.',
    privacyTitle: 'Tietosuojaseloste | pAIoneer Labs GmbH',
    privacyDescription: 'Tietosuojatiedot: paioneer-labs.eu, evästeet, paikalliset palvelut, Kyveilin esittely, lataukset ja ammatilliset yhteydenotot.',
    aiTransparencyTitle: 'Tekoälyn läpinäkyvyys | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Miten pAIoneer Labs GmbH käyttää tekoälyä ja tiedottaa siitä: tekoälyavustaja, tekoälyn tuottaman sisällön merkitseminen, toimituksellinen vastuu ja yhteyshenkilö tekoälyasetuksen mukaisesti.',
    impressumTitle: 'Oikeudellinen ilmoitus | pAIoneer Labs GmbH',
    impressumDescription: 'pAIoneer Labs GmbH:n lakisääteiset palveluntarjoajan tiedot.',
    europe: 'Eurooppa',
    market: 'markkina',
    defence: 'puolustus, kriittinen infrastruktuuri, joukkojen suojaaminen, satamat, energialaitokset, varastot ja rajan läheiset kohteet',
    defenceAi: 'Puolustusalan tekoälykonsultointi Euroopassa: paikalliset LLM-mallit, agenttinen kehitys omassa konesalissa (on-premises), turvallisuusluokitellun tiedon työnkulut, päättely reunalaitteissa, RAG-arviointi ja hallittu tekoälyn käyttöönotto.',
    aiConsulting: 'Tekoälykonsultointi Euroopassa: käytännön käyttötapaukset, ROI-arviointi, paikallisten LLM-mallien käyttöönotto, RAG- ja GraphRAG-arkkitehtuurit, reunatekoäly, AI Act -valmius ja ohjelmistointegraatio.',
    kyveilEurope: 'Kyveilin PII-anonymisointi Euroopassa: paikallinen, GDPR:n vaatimukset täyttävä dokumenttien anonymisointi ennen tekoälytyönkulkuja, testejä, analytiikkaa, data-aineistojen käyttöä ja jakamista ulkopuolisille.',
    sectionsTitle: 'Ydinaiheet',
    relatedTitle: 'Aiheeseen liittyvät teemasivut',
    contactCta: 'Ota yhteyttä pAIoneer Labs GmbH:hon'
  },
  ja: {
    siteTagline: 'ローカルAIソフトウェア、データ保護、ソフトウェアアーキテクチャ',
    homeTitle: 'pAIoneer Labs GmbH - データ保護を重視したローカルAIソリューション',
    homeDescription: 'pAIoneer Labs GmbHは、データ保護を重視したローカルAIソフトウェアを開発しています。個人データ匿名化ソフトウェアKyveil、企業AI Veridion Agentic Company Intelligence、Intent-Driven Development、AI研修、AIトレーディングアシスタントを提供します。',
    teamTitle: 'チーム | pAIoneer Labs GmbH',
    teamDescription: 'Adrian EisenmeierとNikolai Genovは、pAIoneer Labs GmbHを創業して代表取締役として経営しており、Alina Tselininaはシニアソフトウェア開発者としてチームに所属しています。',
    eventsTitle: 'イベント | pAIoneer Labs GmbH',
    eventsDescription: 'pAIoneer Labs GmbHが参加する、または参加したイベント。',
    kyveilTitle: 'Kyveil PII匿名化 | ローカルで動作するデータ保護ソフトウェア',
    kyveilDescription: 'Kyveilは、AI、テスト、分析、外部共有に先立ち、文書やデータセットに含まれる個人を識別できる情報をローカルで検出し、匿名化します。',
    iddTitle: 'エージェント型コーディングのためのIntent-Driven Development（IDD）',
    iddDescription: 'IDDは、目標、非目標、意思決定、シナリオをコーディングエージェントが活用できるプロジェクトの記憶に変え、AI支援ソフトウェア開発を制御可能にします。',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridionはビジネスユニットに編成されたAI社員による完全な組織です。固定サイクルで計画し、検証済みの成果を昼夜なく届け、足りないAIシステムは自ら構築し、オンプレミスまたは自社クラウドで稼働します。',
    quantumTitle: "ソブリンLLM＆量子AI | pAIoneer Labs GmbH",
    quantumDescription: "ドイツ発の目標：Veridion（「AIがAIをさらに進化させる」）のもと、将来的に米中の大規模モデルと競合する独自のコンパクト言語モデルと量子トランスフォーマーを開発します。",
    teachingTitle: 'AI研修、エッジAI、EU AI Actワークショップ',
    teachingDescription: '経営層、プロジェクトマネージャー、開発者を対象とした実践的なAI研修。LLM、RAG、エッジAI、AI Actへの準拠、セキュリティ、導入戦略を扱います。',
    financeTitle: 'AIトレーディングアシスタント：対話によるデータ分析、予測、センチメント',
    financeDescription: 'AIトレーディングアシスタントは、お使いの証券口座と連携し、価格、予測、金融ニュースの分析と金融商品ごとの機械学習モデルをもとに、保有ポジションに関する質問に普段の言葉で答えます。',
    privacyTitle: 'プライバシーポリシー | pAIoneer Labs GmbH',
    privacyDescription: 'paioneer-labs.eu、Cookie、ローカルサービス、Kyveilの紹介、ダウンロード、業務上のお問い合わせに関するデータ保護情報。',
    aiTransparencyTitle: 'AIの透明性 | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'pAIoneer Labs GmbHによる人工知能の利用と、それに関する情報提供について：AIアシスタント、AI生成コンテンツの表示、編集上の責任、AI法に基づくお問い合わせ先。',
    impressumTitle: '法的表示 | pAIoneer Labs GmbH',
    impressumDescription: 'pAIoneer Labs GmbHの法定の提供者情報。',
    europe: '欧州',
    market: '市場',
    defence: '防衛, 重要インフラ, 部隊防護, 港湾, エネルギー施設, 補給拠点, 国境付近の施設',
    defenceAi: '欧州向けの防衛AIコンサルティング。ローカルLLM、オンプレミスでのエージェント型開発、機密データを扱うワークフロー、エッジ推論、RAG評価、統制の取れたAI導入を支援します。',
    aiConsulting: '欧州におけるAIコンサルティング。実践的なユースケース、ROI評価、ローカルLLMの導入、RAGおよびGraphRAGのアーキテクチャ、エッジAI、AI Actへの対応準備、ソフトウェア統合を支援します。',
    kyveilEurope: '欧州向けのKyveil PII匿名化。AIワークフロー、テスト、分析、データセット、外部共有に先立ち、GDPRに対応した文書の匿名化をローカルで行います。',
    sectionsTitle: '主要テーマ',
    relatedTitle: '関連する重点ページ',
    contactCta: 'pAIoneer Labs GmbHに問い合わせる'
  },
  pl: {
    siteTagline: 'Lokalne oprogramowanie AI, ochrona danych i architektura oprogramowania',
    homeTitle: 'pAIoneer Labs GmbH – lokalne rozwiązania AI z naciskiem na ochronę danych',
    homeDescription: 'pAIoneer Labs GmbH tworzy lokalne oprogramowanie AI z naciskiem na ochronę danych: anonimizacja danych osobowych Kyveil, firmowa AI Veridion Agentic Company Intelligence, Intent-Driven Development, szkolenia z AI oraz asystent tradingowy AI.',
    teamTitle: 'Zespół | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier i Nikolai Genov założyli pAIoneer Labs GmbH i kierują nią jako dyrektorzy zarządzający; Alina Tselinina należy do zespołu jako Senior Software Developer.',
    eventsTitle: 'Wydarzenia | pAIoneer Labs GmbH',
    eventsDescription: 'Wydarzenia, w których pAIoneer Labs GmbH uczestniczy lub uczestniczyła.',
    kyveilTitle: 'Kyveil – anonimizacja PII | lokalne oprogramowanie do ochrony danych',
    kyveilDescription: 'Kyveil lokalnie wykrywa i anonimizuje dane osobowe w dokumentach i zbiorach danych przed użyciem w AI, testach i analizach lub przed udostępnieniem na zewnątrz.',
    iddTitle: 'Intent-Driven Development (IDD) dla Agentic Coding',
    iddDescription: 'IDD pozwala sterować wytwarzaniem oprogramowania wspomaganym przez AI: cele, cele wykluczone, decyzje i scenariusze stają się operacyjną pamięcią projektu dla agentów kodujących.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion to kompletna organizacja pracowników AI w jednostkach biznesowych: planuje w stałych cyklach, całą dobę dostarcza zweryfikowane rezultaty, brakujące systemy AI buduje sama i działa on-premises lub we własnej chmurze.',
    quantumTitle: "Suwerenne LLM i Kwantowe AI | pAIoneer Labs GmbH",
    quantumDescription: "Nasz cel z Niemiec: pod nadzorem Veridion („AI rozwija AI dalej”) stworzyć własne kompaktowe modele językowe i Kwantowe Transformery, które w przyszłości będą konkurować z wielkimi modelami z USA i Chin.",
    teachingTitle: 'Szkolenia z AI, edge AI i warsztaty z EU AI Act',
    teachingDescription: 'Praktyczne szkolenia z AI dla kadry zarządzającej, kierowników projektów i programistów: modele LLM, RAG, edge AI, zgodność z AI Act, bezpieczeństwo i strategia wdrożenia.',
    financeTitle: 'Asystent tradingowy AI: analiza danych w rozmowie, prognozy i sentyment',
    financeDescription: 'Asystent tradingowy AI łączy się z Twoim rachunkiem maklerskim i odpowiada zwykłym językiem na pytania o Twoje pozycje, z analizami kursów, prognoz i wiadomości finansowych oraz modelem uczenia maszynowego dla każdego instrumentu finansowego.',
    privacyTitle: 'Polityka prywatności | pAIoneer Labs GmbH',
    privacyDescription: 'Informacje o ochronie danych dla paioneer-labs.eu: pliki cookie, usługi lokalne, prezentacja Kyveil, pliki do pobrania i kontakt biznesowy.',
    aiTransparencyTitle: 'Przejrzystość w zakresie AI | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Jak pAIoneer Labs GmbH wykorzystuje sztuczną inteligencję i informuje o tym: asystent AI, oznaczanie treści wygenerowanych przez AI, odpowiedzialność redakcyjna i punkt kontaktowy zgodnie z aktem w sprawie sztucznej inteligencji.',
    impressumTitle: 'Nota prawna | pAIoneer Labs GmbH',
    impressumDescription: 'Informacje prawne o usługodawcy pAIoneer Labs GmbH.',
    europe: 'Europa',
    market: 'rynek',
    defence: 'obronność, infrastruktura krytyczna, ochrona wojsk, porty, obiekty energetyczne, składy i obiekty przygraniczne',
    defenceAi: 'Doradztwo w zakresie AI dla obronności w Europie: lokalne modele LLM, agentowe wytwarzanie oprogramowania on-premises, przepływy pracy z danymi niejawnymi, inferencja na brzegu sieci, ewaluacja RAG i kontrolowane wdrażanie AI.',
    aiConsulting: 'Doradztwo w zakresie AI w Europie: praktyczne przypadki użycia, ocena ROI, wdrażanie lokalnych modeli LLM, architektury RAG i GraphRAG, edge AI, gotowość na AI Act i integracja oprogramowania.',
    kyveilEurope: 'Kyveil – anonimizacja PII dla Europy: lokalna, zgodna z RODO anonimizacja dokumentów przed przepływami pracy z AI, testami, analizami, tworzeniem zbiorów danych i udostępnianiem na zewnątrz.',
    sectionsTitle: 'Kluczowe tematy',
    relatedTitle: 'Powiązane strony tematyczne',
    contactCta: 'Skontaktuj się z pAIoneer Labs GmbH'
  },
  bg: {
    siteTagline: 'Локален ИИ софтуер, защита на данните и софтуерна архитектура',
    homeTitle: 'pAIoneer Labs GmbH - локални ИИ решения с фокус върху защитата на данните',
    homeDescription: 'pAIoneer Labs GmbH разработва локален ИИ софтуер с фокус върху защитата на данните: анонимизация на лични данни с Kyveil, фирмения ИИ Veridion Agentic Company Intelligence, Intent-Driven Development, обучение по ИИ и ИИ асистент за трейдинг.',
    teamTitle: 'Екип | pAIoneer Labs GmbH',
    teamDescription: 'Adrian Eisenmeier и Nikolai Genov основаха pAIoneer Labs GmbH и го ръководят като управители; Alina Tselinina е част от екипа като Senior Software Developer.',
    eventsTitle: 'Събития | pAIoneer Labs GmbH',
    eventsDescription: 'Събития, в които pAIoneer Labs GmbH участва или е участвало.',
    kyveilTitle: 'Kyveil – анонимизация на лични данни (PII) | Локален софтуер за защита на данните',
    kyveilDescription: 'Kyveil разпознава и анонимизира лични данни локално в документи и набори от данни – преди използване с ИИ, тестове, анализи или външно споделяне.',
    iddTitle: 'Intent-Driven Development (IDD) за агентно програмиране',
    iddDescription: 'IDD прави софтуерната разработка с помощта на ИИ управляема, като превръща целите, това, което не е цел, решенията и сценариите в оперативна проектна памет за кодовите агенти.',
    veridionTitle: 'Veridion Agentic Company Intelligence',
    veridionDescription: 'Veridion е цялостна организация от ИИ служители в бизнес звена: планира във фиксирани цикли, доставя денонощно проверени резултати, сама изгражда липсващите ИИ системи и работи на място или във Вашия собствен облак.',
    quantumTitle: "Суверенни LLM и Квантов ИИ | pAIoneer Labs GmbH",
    quantumDescription: "Нашата цел от Германия: под управлението на Veridion („ИИ развива ИИ напред“) да разработим компактни езикови модели и квантови трансформъри, които в бъдеще ще се конкурират с големите модели от САЩ и Китай.",
    teachingTitle: 'Обучения по ИИ, периферен ИИ (Edge AI) и уъркшопи по EU AI Act',
    teachingDescription: 'Практически обучения по ИИ за висши ръководители, ръководители на проекти и разработчици: LLM, RAG, периферен ИИ, съответствие с AI Act, сигурност и стратегия за внедряване.',
    financeTitle: 'ИИ асистент за трейдинг: анализ на данни в разговор, прогнози и сентимент',
    financeDescription: 'ИИ асистентът за трейдинг се свързва с Вашата брокерска сметка и отговаря на въпроси за позициите Ви на естествен език, с анализи на курсове, прогнози и финансови новини и с модел за машинно обучение за всеки финансов инструмент.',
    privacyTitle: 'Политика за поверителност | pAIoneer Labs GmbH',
    privacyDescription: 'Информация за защитата на данните за paioneer-labs.eu, бисквитките, локалните услуги, представянето на Kyveil, файловете за изтегляне и професионалния контакт.',
    aiTransparencyTitle: 'Прозрачност относно ИИ | pAIoneer Labs GmbH',
    aiTransparencyDescription: 'Как pAIoneer Labs GmbH използва изкуствен интелект и информира за това: асистент с ИИ, обозначаване на съдържание, генерирано с ИИ, редакционна отговорност и лице за контакт съгласно Акта за изкуствения интелект.',
    impressumTitle: 'Правна информация | pAIoneer Labs GmbH',
    impressumDescription: 'Правна информация за доставчика pAIoneer Labs GmbH.',
    europe: 'Европа',
    market: 'пазар',
    defence: 'отбрана, критична инфраструктура, защита на силите, пристанища, енергийни обекти, складове и обекти в близост до граници',
    defenceAi: 'Консултиране по ИИ за отбраната в Европа: локални LLM, агентна разработка в собствена инфраструктура, работни процеси с класифицирани данни, инференция на периферни устройства, оценяване на RAG и контролирано внедряване на ИИ.',
    aiConsulting: 'Консултиране по ИИ в Европа за практически случаи на употреба, оценка на възвръщаемостта (ROI), локално внедряване на LLM, архитектури RAG и GraphRAG, периферен ИИ, готовност за AI Act и софтуерна интеграция.',
    kyveilEurope: 'Kyveil – анонимизация на лични данни (PII) за Европа: локална, съобразена с GDPR анонимизация на документи преди работни процеси с ИИ, тестове, анализи, набори от данни и външно споделяне.',
    sectionsTitle: 'Основни теми',
    relatedTitle: 'Свързани тематични страници',
    contactCta: 'Свържете се с pAIoneer Labs GmbH'
  }
};

const pageCopyKeys = {
  home: ['homeTitle', 'homeDescription'],
  team: ['teamTitle', 'teamDescription'],
  events: ['eventsTitle', 'eventsDescription'],
  kyveil: ['kyveilTitle', 'kyveilDescription'],
  agenticdev: ['iddTitle', 'iddDescription'],
  veridion: ['veridionTitle', 'veridionDescription'],
  quantum: ['quantumTitle', 'quantumDescription'],
  teaching: ['teachingTitle', 'teachingDescription'],
  finance: ['financeTitle', 'financeDescription'],
  privacy: ['privacyTitle', 'privacyDescription'],
  aiTransparency: ['aiTransparencyTitle', 'aiTransparencyDescription'],
  impressum: ['impressumTitle', 'impressumDescription']
};

const clusterLabels = {
  defenceai: 'defence AI, defense AI, local LLM, on-premises AI, classified data, agentic development, RAG evaluation, edge inference',
  ai: 'AI consulting, local LLM, RAG, GraphRAG, edge AI, AI Act, software architecture, AI implementation',
  kyveil: 'Kyveil, PII anonymization, GDPR, local data protection, document anonymization, AI privacy, sensitive documents'
};

const baseKeywords = 'pAIoneer Labs GmbH, local AI, data protection, software architecture, Kyveil, PII anonymization, Veridion Agentic Company Intelligence, Intent-Driven Development, IDD, legacy software migration, AI training, AI trading assistant';

const normalizeLanguage = language => (languageCodes.includes(language) ? language : DEFAULT_LANGUAGE);

const getPageIdBySlug = slug => {
  const normalizedSlug = (slug || '').replace(/^\/+|\/+$/g, '');
  return Object.keys(pages).find(pageId => pages[pageId].slug === normalizedSlug) || 'home';
};

const getPathForPage = (language, pageId) => {
  const lang = normalizeLanguage(language);
  const page = pages[pageId] || pages.home;
  return page.slug ? `/${lang}/${page.slug}` : `/${lang}`;
};

const getAbsoluteUrl = (language, pageId) => `${BASE_URL}${getPathForPage(language, pageId)}`;

const getAlternates = pageId => {
  const alternates = languages.map(language => ({
    lang: language.hreflang,
    code: language.code,
    href: getAbsoluteUrl(language.code, pageId)
  }));

  alternates.push({ lang: 'x-default', code: DEFAULT_LANGUAGE, href: getAbsoluteUrl(DEFAULT_LANGUAGE, pageId) });
  return alternates;
};

const getLocalizedCopy = language => localized[normalizeLanguage(language)] || localized[DEFAULT_LANGUAGE];

const getTopicItems = copy => [
  copy.kyveilDescription,
  copy.iddDescription,
  copy.teachingDescription,
  copy.financeDescription
];

const getMarketSeo = (pageId, language) => {
  const page = pages[pageId];
  const copy = getLocalizedCopy(language);
  const descriptionByCluster = {
    defenceai: copy.defenceAi,
    ai: copy.aiConsulting,
    kyveil: copy.kyveilEurope
  };
  const labelByCluster = {
    defenceai: 'Defence AI Europe',
    ai: 'AI Consulting Europe',
    kyveil: 'Kyveil PII Anonymization Europe'
  };
  const label = labelByCluster[page.cluster] || page.slug;
  const description = descriptionByCluster[page.cluster] || copy.homeDescription;
  const sectorKeywords = page.cluster === 'defenceai' ? `, ${copy.defence}` : '';

  return {
    title: `${label} | pAIoneer Labs GmbH`,
    description,
    h1: label,
    lead: description,
    keywords: `${clusterLabels[page.cluster]}, ${copy.europe}, pAIoneer Labs GmbH${sectorKeywords}`,
    sections: [
      {
        title: copy.sectionsTitle,
        items: [
          description,
          copy.kyveilDescription,
          copy.iddDescription
        ]
      }
    ]
  };
};

// A member's page takes its name and summary from the profile source, so the
// search-engine text of a profile cannot differ from the profile itself.
const getMemberSeo = (pageId, language) => {
  const copy = getLocalizedCopy(language);
  const member = teamMembers.find(({ slug }) => slug === pages[pageId].member);
  const profile = member.profiles[normalizeLanguage(language)];

  return {
    title: `${member.displayName} | pAIoneer Labs GmbH`,
    description: profile.summary,
    h1: member.displayName,
    lead: profile.summary,
    keywords: `${member.displayName}, ${baseKeywords}`,
    sections: [
      {
        title: copy.sectionsTitle,
        items: [profile.hero.kicker, profile.hero.subtitle].filter(Boolean)
      }
    ]
  };
};

const getBasePageSeo = (pageId, language) => {
  const copy = getLocalizedCopy(language);
  const [titleKey, descriptionKey] = pageCopyKeys[pageId] || pageCopyKeys.home;
  const title = copy[titleKey];
  const description = copy[descriptionKey];
  const h1 = title.replace(/\s+\|\s+pAIoneer Labs GmbH$/, '');
  const documentTitle = title.includes('pAIoneer Labs GmbH') ? title : `${title} | pAIoneer Labs GmbH`;

  return {
    title: documentTitle,
    description,
    h1,
    lead: description,
    keywords: baseKeywords,
    sections: [
      {
        title: copy.sectionsTitle,
        items: getTopicItems(copy)
      }
    ]
  };
};

const getStructuredData = (pageId, language, seo) => {
  const page = pages[pageId] || pages.home;
  const url = getAbsoluteUrl(language, pageId);
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'pAIoneer Labs GmbH', item: getAbsoluteUrl(language, 'home') }
  ];

  if (page.member) {
    breadcrumbItems.push({ '@type': 'ListItem', position: 2, name: getBasePageSeo('team', language).h1, item: getAbsoluteUrl(language, 'team') });
    breadcrumbItems.push({ '@type': 'ListItem', position: 3, name: seo.h1, item: url });
  } else if (pageId !== 'home') {
    breadcrumbItems.push({ '@type': 'ListItem', position: 2, name: seo.h1, item: url });
  }

  const founderId = `${BASE_URL}/#${FOUNDER_SLUG}`;

  const common = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Person',
        '@id': founderId,
        name: 'Adrian Eisenmeier',
        jobTitle: 'Founder and Managing Director of pAIoneer Labs GmbH',
        url: getAbsoluteUrl(language, memberPageId(FOUNDER_SLUG)),
        image: teamPhotoUrl(FOUNDER_SLUG),
        sameAs: [
          'https://www.linkedin.com/in/adrian-eisenmeier-725a011b5/',
          'https://x.com/pAIoneer_Labs_GmbH_info',
          'https://www.facebook.com/adrian.eisenmeier.7',
          'https://www.xing.com/profile/Adrian_Eisenmeier/web_profiles'
        ]
      },
      {
        '@type': 'Organization',
        '@id': `${BASE_URL}/#organization`,
        name: 'pAIoneer Labs GmbH',
        url: BASE_URL,
        logo: LOGO_IMAGE,
        founder: { '@id': founderId }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumbItems
      }
    ]
  };

  const typedData = {
    '@type': page.type,
    '@id': `${url}#primary`,
    name: seo.h1,
    headline: seo.h1,
    description: seo.description,
    url,
    image: seo.image || OG_IMAGE,
    provider: { '@id': `${BASE_URL}/#organization` },
    author: { '@id': `${BASE_URL}/#organization` },
    isPartOf: { '@id': `${BASE_URL}/#website` }
  };

  if (page.type === 'Service') {
    typedData.serviceType = seo.h1;
    typedData.areaServed = ['Germany', 'Netherlands', 'Poland', 'Ukraine', 'Bulgaria', 'European Union'];
  }

  if (page.type === 'SoftwareApplication') {
    typedData.applicationCategory = { finance: 'FinanceApplication', veridion: 'BusinessApplication' }[pageId] || 'SecurityApplication';
    typedData.operatingSystem = 'Windows, macOS, Linux, Server';
  }

  if (page.member) {
    const member = teamMembers.find(({ slug }) => slug === page.member);
    typedData.mainEntity = page.member === FOUNDER_SLUG
      ? { '@id': founderId }
      : {
          '@type': 'Person',
          name: member.name,
          image: teamPhotoUrl(member.slug),
          url,
          worksFor: { '@id': `${BASE_URL}/#organization` }
        };
  }

  common['@graph'].push({
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    name: 'pAIoneer Labs GmbH',
    url: BASE_URL,
    inLanguage: language,
    publisher: { '@id': `${BASE_URL}/#organization` }
  });
  common['@graph'].push(typedData);

  return common;
};

const getPageSeo = (pageId, language = DEFAULT_LANGUAGE) => {
  const lang = normalizeLanguage(language);
  const page = pages[pageId] || pages.home;
  const resolvedPageId = pages[pageId] ? pageId : 'home';
  const pageSeo = page.market
    ? getMarketSeo(resolvedPageId, lang)
    : page.member
      ? getMemberSeo(resolvedPageId, lang)
      : getBasePageSeo(resolvedPageId, lang);
  const image = page.member ? teamPhotoUrl(page.member) : OG_IMAGE;
  const baseSeo = { ...pageSeo, image };
  const url = getAbsoluteUrl(lang, resolvedPageId);
  const languageConfig = languages.find(({ code }) => code === lang) || languages.find(({ code }) => code === DEFAULT_LANGUAGE);

  return {
    ...baseSeo,
    pageId: resolvedPageId,
    language: lang,
    url,
    canonical: url,
    ogTitle: baseSeo.title,
    ogDescription: baseSeo.description,
    ogImage: image,
    ogType: page.type === 'ProfilePage' ? 'profile' : 'website',
    ogLocale: languageConfig.locale,
    twitterTitle: baseSeo.title,
    twitterDescription: baseSeo.description,
    twitterImage: image,
    alternates: getAlternates(resolvedPageId),
    structuredData: getStructuredData(resolvedPageId, lang, baseSeo),
    changefreq: page.changefreq,
    priority: page.priority,
    slug: page.slug,
    type: page.type
  };
};

const getPageSeoFromPath = (pathname = '/', language = DEFAULT_LANGUAGE) => {
  const parts = pathname.split('/').filter(Boolean);
  const firstPart = parts[0];
  const languageFromPath = languageCodes.includes(firstPart) ? firstPart : language;
  const slugParts = languageCodes.includes(firstPart) ? parts.slice(1) : parts;
  const slug = slugParts.join('/');
  return getPageSeo(getPageIdBySlug(slug), languageFromPath);
};

const getRelatedPageIds = pageId => {
  if (pageId === 'home') return ['kyveil', 'veridion', 'finance', 'agenticdev', 'quantum', ...strategicPageIds];
  return strategicPageIds;
};

module.exports = {
  BASE_URL,
  DEFAULT_LANGUAGE,
  OG_IMAGE,
  languages,
  languageCodes,
  pages,
  navPageIds,
  indexablePageIds,
  strategicPageIds,
  getAbsoluteUrl,
  getAlternates,
  getLocalizedCopy,
  getPageIdBySlug,
  getPageSeo,
  getPageSeoFromPath,
  getPathForPage,
  getRelatedPageIds,
  normalizeLanguage
};
